import { X } from "lucide-react";

import { Button } from "../../Button";
import { DateTimePicker } from "../../DateTimePicker";
import { Label } from "../../Label";
import { Separator } from "../../Separator";

export type DateFilter =
  | { type: "date"; mode: "after" | "before"; date: string }
  | { type: "date"; mode: "range"; from?: string; to?: string };

interface IDateFilterInputProps {
  value: DateFilter | undefined;
  onChange: (value: DateFilter | undefined) => void;
}

export function DateFilterInput({ value, onChange }: IDateFilterInputProps) {
  const startDate =
    value?.mode === "range"
      ? value.from
        ? new Date(value.from)
        : undefined
      : value?.mode === "after" && value.date
        ? new Date(value.date)
        : undefined;
  const endDate =
    value?.mode === "range"
      ? value.to
        ? new Date(value.to)
        : undefined
      : value?.mode === "before" && value.date
        ? new Date(value.date)
        : undefined;

  const emit = (from: Date | undefined, to: Date | undefined) => {
    if (!from && !to) {
      onChange(undefined);
      return;
    }

    onChange({
      type: "date",
      mode: "range",
      from: from?.toISOString(),
      to: to?.toISOString(),
    });
  };

  return (
    <div className="flex items-end gap-2">
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-1">
          <Label className="text-xs font-medium text-gray-600">Start</Label>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 p-0 text-gray-400 hover:text-gray-700"
            onClick={() => emit(undefined, endDate)}
            disabled={!startDate}
          >
            <X size={12} />
          </Button>
        </div>
        <DateTimePicker
          granularity="day"
          value={startDate}
          onChange={(nextStart) => emit(nextStart, endDate)}
          placeholder="Start date"
          className="min-w-[11rem]"
        />
      </div>
      <div className="pb-1.5">
        <Separator orientation="vertical" className="h-6" />
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-1">
          <Label className="text-xs font-medium text-gray-600">End</Label>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 p-0 text-gray-400 hover:text-gray-700"
            onClick={() => emit(startDate, undefined)}
            disabled={!endDate}
          >
            <X size={12} />
          </Button>
        </div>
        <DateTimePicker
          granularity="day"
          value={endDate}
          onChange={(nextEnd) => emit(startDate, nextEnd)}
          placeholder="End date"
          className="min-w-[11rem]"
        />
      </div>
    </div>
  );
}
